import classNames from 'clsx';
import type { MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { fbPixelCustomEvents, fbTrack, fbTrackSignIn } from 'src/client/analytics/facebook/fbTrack';
import { recaptchaActions, recaptchaV2SiteKey } from 'src/common/constants/auth/recaptcha';
import { AUTH_EMAIL_SIGNIN_RECAPTCHA_ENABLED } from 'src/common/constants/experiments/auth';
import {
  FORGOT_PASSWORD_LINK,
  NEXT,
  RECAPTCHA_V3_FAILED,
  REMEMBER_ME_LABEL,
  SIGN_IN,
  SIGN_IN_FAILED,
  USERNAME_EMAIL,
  USERNAME_EMAIL_REQUIRED,
} from 'src/common/constants/localizations/auth';
import { PASSWORD, PASSWORD_REQUIRED } from 'src/common/constants/localizations/shared';
import { SOURCE_ONAIR } from 'src/common/constants/sourceQueryParams';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import useOnMount from 'src/common/hooks/useOnMount';
import { getPartnerParams } from 'src/common/utils/auth/partnerAuthFlowHelpers';
import { getRecaptchaToken, loadRecaptchaV2Script } from 'src/common/utils/auth/recaptcha';
import { logClientError, logSignInClick } from '../../actions/logging';
import account from '../../constants/analytics/categoryActionLabel/account';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { isRequired } from '../../utils/form/validation';
import vars from '../../vars';
import InkButton from '../shared/button/InkButton';
import PremiumGoldButton from '../shared/button/PremiumGoldButton';
import ShowPasswordButton from '../shared/button/ShowPasswordButton';
import CheckBox from '../shared/form/CheckBox';
import FormInput from '../shared/form/FormInput';
import TopLevelErrorContainer from '../shared/form/TopLevelErrorContainer';
import UnderlineLink from '../shared/link/UnderlineLink';
import partnerAuthSharedStyles, {
  inputCheckmarkSvgProps,
} from '../shared/styles/partnerAuthSharedStyles';
import Checkmark from '../shared/svgIcons/LegacyCheckmark';
import checkFieldsForValidInput from '../utils/checkFieldsForValidInput';
import authCss from './auth.module.scss';
import AuthFlowPromoOfferFinePrint from './shared/AuthFlowPromoOfferFinePrint';
import AuthSectionDivider from './shared/AuthSectionDivider';
import GeneralNotices from './shared/GeneralNotices';
import OauthButtons from './shared/OauthButtons';
import SecondaryContent from './shared/SecondaryContent';
import css from './sign-in-view.module.scss';

const fieldOrder = ['usernameEmail', 'password'];

const svgProps = {
  ...inputCheckmarkSvgProps,
  className: authCss.checkmark,
};

type SignInFormValues = {
  usernameEmail?: string;
  password?: string;
  rememberUser?: boolean;
};

type OauthParams = [
  string, // provider name
  { token: string; userId: string; firstName?: string; lastName?: string }, // data
  string, // source
  Record<string, unknown>, // params
];

// TODO: this type should be tied to zustand/redux action
interface LoginWithEmailParams {
  data: {
    username: string;
    password: string;
    rememberUser?: boolean;
    recaptchaV3Token?: string | null;
    recaptchaV2Token?: string | null;
    source?: string;
  };
  params?: Record<string, string>;
  viewTypeData: Record<string, string>;
}

interface SignInActions {
  loginWithEmail: (params: LoginWithEmailParams) => Promise<void>;
  loginWithOauth: (
    ...args: [...OauthParams, { viewTypeData: Record<string, string> }]
  ) => Promise<void>;
}

type QueryProduct = {
  freeTrialLength?: number;
} | null;

interface SignInViewProps {
  actions: SignInActions;
  viewTypeData: Record<string, string>;
  queryProduct?: QueryProduct;
  handleChangeToSignUp?: () => void;
  failAuthNotice?: string;
  successAuthNotice?: string;
  handleChangeToForgotPassword?: () => void;
  isDialog?: boolean;
  isPartner?: boolean;
  source?: string;
  isPaymentFlow?: boolean;
}

type LocalizationContextValue = {
  location?: {
    query?: Record<string, unknown>;
    [key: string]: unknown;
  } | null;
  getLocalizedText: (name: string, values?: Record<string, unknown>) => string;
};

function SignInViewComponent({
  actions,
  viewTypeData,
  queryProduct,
  handleChangeToSignUp,
  failAuthNotice,
  successAuthNotice,
  handleChangeToForgotPassword,
  isDialog = false,
  isPartner = false,
  source,
  isPaymentFlow = false,
}: SignInViewProps) {
  const isRecaptchaV3Enabled = useExperiment(AUTH_EMAIL_SIGNIN_RECAPTCHA_ENABLED, false);
  const logActions = useActions({ logSignInClick, logClientError });
  const locationContext = useContext(LocationAndLocalizationContext) as
    | LocalizationContextValue
    | undefined;
  const { location, getLocalizedText } = locationContext ?? {
    location: undefined,
    getLocalizedText: (name: string) => name,
  };

  const [showPassword, setShowPassword] = useState(false);
  const [shouldRenderRecaptchaV2, setShouldRenderRecaptchaV2] = useState(false);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState<string | null>(null);

  const recaptchaV2ContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaV2WidgetIdRef = useRef<number | null>(null);

  useOnMount(() => {
    fbTrack(fbPixelCustomEvents.SignInPageVisit);
  });

  const renderRecaptchaV2 = useCallback(async () => {
    const container = recaptchaV2ContainerRef.current;

    if (!container) {
      return;
    }

    try {
      await loadRecaptchaV2Script();
    } catch (error) {
      logActions.logClientError({
        message: 'Recaptcha V2: failed to load script',
        context: { error },
      });
      return;
    }

    if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
      logActions.logClientError({
        message: 'Recaptcha V2: grecaptcha unavailable after script load',
        context: {},
      });
      return;
    }

    if (!recaptchaV2SiteKey) {
      logActions.logClientError({
        message: 'Recaptcha V2: site key missing',
        context: {},
      });
      return;
    }

    if (recaptchaV2WidgetIdRef.current !== null) {
      setRecaptchaV2Token(null);
      window.grecaptcha.reset(recaptchaV2WidgetIdRef.current);
      return;
    }

    function handleRecaptchaV2Error(error: Error) {
      setRecaptchaV2Token(null);

      if (recaptchaV2WidgetIdRef.current !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(recaptchaV2WidgetIdRef.current);
      }

      logActions.logClientError({
        message: 'Recaptcha V2: widget error callback triggered',
        context: { error },
      });
    }

    try {
      recaptchaV2WidgetIdRef.current = window.grecaptcha.render(container, {
        sitekey: recaptchaV2SiteKey,
        callback: setRecaptchaV2Token,
        'expired-callback': handleRecaptchaV2Error,
        'error-callback': handleRecaptchaV2Error,
      });
    } catch (error) {
      logActions.logClientError({
        message: 'Recaptcha V2: failed to render widget',
        context: { error },
      });
    }
  }, [logActions.logClientError]);

  useEffect(() => {
    if (shouldRenderRecaptchaV2) {
      renderRecaptchaV2();
    }
  }, [renderRecaptchaV2, shouldRenderRecaptchaV2]);

  const triggerRecaptchaV2Fallback = useCallback(() => {
    setRecaptchaV2Token(null);

    if (shouldRenderRecaptchaV2) {
      renderRecaptchaV2();
      return;
    }

    setShouldRenderRecaptchaV2(true);
  }, [renderRecaptchaV2, shouldRenderRecaptchaV2]);

  const loginWithEmail = useCallback(
    async (authActionParams: LoginWithEmailParams) => {
      try {
        await actions.loginWithEmail(authActionParams);
      } catch (error) {
        // TODO: We need to fix HTTPError type in monorepo - it doesn't allow the body customization i.e. to look for `code` field
        if ((error as any)?.body?.code === 'RECAPTCHA_V3_FAILED') {
          triggerRecaptchaV2Fallback();

          return {
            base: RECAPTCHA_V3_FAILED,
          };
        }

        if (recaptchaV2WidgetIdRef.current !== null && window.grecaptcha?.reset) {
          setRecaptchaV2Token(null);
          window.grecaptcha.reset(recaptchaV2WidgetIdRef.current);
        }

        return {
          base: SIGN_IN_FAILED,
        };
      }
    },
    [actions, triggerRecaptchaV2Fallback],
  );

  const handleOauthSignIn = useCallback(
    async (...args: OauthParams) => {
      const [providerName, data, source, params] = args;

      actions.loginWithOauth(providerName, data, source, params, { viewTypeData });
    },
    [actions, viewTypeData],
  );

  const handleShowPasswordClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowPassword((prev) => !prev);
  }, []);

  const onSubmit = useCallback(
    async (values: SignInFormValues) => {
      const { usernameEmail: username, password, rememberUser } = values;

      if (!username || !password) {
        return undefined;
      }

      const recaptchaV3Token =
        isRecaptchaV3Enabled && recaptchaV2ContainerRef.current === null
          ? await getRecaptchaToken(recaptchaActions.emailSignIn, logActions.logClientError)
          : null;

      if (recaptchaV2ContainerRef.current !== null && !recaptchaV2Token) {
        logActions.logClientError({
          message: 'Recaptcha v2: login attempt blocked due to missing v2 token',
          context: {},
        });

        return { base: RECAPTCHA_V3_FAILED };
      }

      const authActionParams: LoginWithEmailParams = {
        data: { username, password, recaptchaV3Token, recaptchaV2Token, rememberUser, source },
        params: getPartnerParams(isPartner, location?.query) as Record<string, string>,
        viewTypeData,
      };

      fbTrackSignIn();
      logActions.logSignInClick(account.labels.email, source);

      return await loginWithEmail(authActionParams);
    },
    [
      isRecaptchaV3Enabled,
      logActions.logClientError,
      recaptchaV2Token,
      source,
      isPartner,
      location?.query,
      viewTypeData,
      loginWithEmail,
      logActions.logSignInClick,
    ],
  );

  const renderPersonalInfoFields = useCallback(
    (validFieldsMap: Record<string, boolean> | null, values?: SignInFormValues) => {
      if (isPartner) {
        return (
          <>
            <div className={classNames(authCss.fieldsSection, authCss.isPartnerFlow)}>
              <div className="row">
                <div className="col-xs-12">
                  <Field
                    component={FormInput}
                    hideInlineError
                    rootClassName={classNames(
                      authCss.inputField,
                      'usernameField',
                      authCss.isPartnerFlow,
                    )}
                    autoCorrect="off"
                    autoCapitalize="none"
                    id="siUsernameEmail"
                    dataTestId="usernameEmail"
                    name="usernameEmail"
                    validate={isRequired(USERNAME_EMAIL_REQUIRED)}
                    label={getLocalizedText(USERNAME_EMAIL)}
                    required
                    localizeErrorMessages
                  />
                  {validFieldsMap?.usernameEmail && <Checkmark {...svgProps} />}
                </div>
              </div>
            </div>
            <div className={classNames(authCss.fieldsSection, authCss.isPartnerFlow)}>
              <div className="row">
                <div className="col-xs-12">
                  <Field
                    component={FormInput}
                    hideInlineError
                    rootClassName={classNames(
                      authCss.inputField,
                      'passwordField',
                      authCss.isPartnerFlow,
                    )}
                    id="siPassword"
                    dataTestId="password"
                    type="password"
                    name="password"
                    label={getLocalizedText(PASSWORD)}
                    validate={isRequired(PASSWORD_REQUIRED)}
                    required
                    localizeErrorMessages
                  />
                  {validFieldsMap?.password && <Checkmark {...svgProps} />}
                </div>
              </div>
            </div>
          </>
        );
      }

      return (
        <div className={authCss.fieldsSection}>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field
                component={FormInput}
                hideInlineError
                rootClassName={classNames(authCss.inputField, 'usernameField')}
                placeholder={getLocalizedText(USERNAME_EMAIL)}
                autoCorrect="off"
                autoCapitalize="none"
                id="siUsernameEmail"
                dataTestId="usernameEmail"
                name="usernameEmail"
                validate={isRequired(USERNAME_EMAIL_REQUIRED)}
                required
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              <Field
                component={FormInput}
                hideInlineError
                rootClassName={classNames(
                  authCss.inputField,
                  'passwordField',
                  authCss.alongWithIcon,
                )}
                id="siPassword"
                dataTestId="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={getLocalizedText(PASSWORD)}
                validate={isRequired(PASSWORD_REQUIRED)}
                required
              />
              {!isPartner && values?.password && (
                <ShowPasswordButton showPassword={showPassword} onClick={handleShowPasswordClick} />
              )}
            </div>
          </div>
        </div>
      );
    },
    [getLocalizedText, handleShowPasswordClick, isPartner, showPassword],
  );

  const renderCheckboxFields = useCallback(() => {
    if (isPartner) {
      return (
        <div className={authCss.mediumSection}>
          <div className={classNames(authCss.mediumSectionInner, authCss.isPartnerFlow)}>
            <Field
              component={CheckBox}
              customSpanClass={classNames(authCss.rememberMeCheckbox, authCss.isPartnerFlow)}
              customCheckmarkClass={classNames(authCss.customCheckmarkClass, authCss.isPartnerFlow)}
              labelClassName={authCss.rememberMeLabel}
              key="rememberUserCheckbox"
              dataTestId="rememberUser"
              id="rememberUser"
              type="checkbox"
              name="rememberUser"
              label={getLocalizedText(REMEMBER_ME_LABEL)}
              labelStyle={partnerAuthSharedStyles.labelStyle}
              isBold
              defaultChecked
            />
            <i key="siOptionsDivider" className={css.authOptionsDivider} />
            <UnderlineLink
              key="siForgotPasswordLink"
              /* biome-ignore lint/a11y/useSemanticElements: invalid lint error for `role` on React Component */
              role="button"
              tabIndex={0}
              id="signInForgotPasswordQuestion"
              onClick={handleChangeToForgotPassword}
              underlineClassName={classNames(
                css.underlineLink,
                css.forgotPassword,
                css.isPartnerFlow,
              )}
              className={classNames(
                authCss.link,
                authCss.forgotPasswordLink,
                authCss.isPartnerFlow,
              )}
              isDropDown
              isNotReactRouterLink
            >
              {getLocalizedText(FORGOT_PASSWORD_LINK)}
            </UnderlineLink>
          </div>
        </div>
      );
    }

    return (
      <div className={authCss.mediumSection}>
        <div className={authCss.mediumSectionInner}>
          <Field
            component={CheckBox}
            customSpanClass={authCss.rememberMeCheckbox}
            customCheckmarkClass={authCss.customCheckmarkClass}
            customCheckmarkUncheckedClass={authCss.customCheckmarkUncheckedClass}
            labelClassName={authCss.rememberMeLabel}
            key="rememberUserCheckbox"
            dataTestId="rememberUser"
            id="rememberUser"
            type="checkbox"
            name="rememberUser"
            label={getLocalizedText(REMEMBER_ME_LABEL)}
            defaultChecked
          />
        </div>
      </div>
    );
  }, [getLocalizedText, handleChangeToForgotPassword, isPartner]);

  const renderTopLevelErrorContainer = useCallback(
    (
      pristine: boolean | undefined,
      touched: Record<string, boolean | undefined> | undefined,
      submitErrors: Record<string, unknown>,
      errors: Record<string, unknown>,
    ) => {
      const safeTouched = touched ?? {};

      return (
        <TopLevelErrorContainer
          className={classNames(authCss.largeSection, {
            [authCss.noSpacing]: !isPartner,
          })}
          customErrorListClass={classNames(authCss.customErrorListClass, {
            [authCss.isPartnerFlow]: isPartner,
          })}
          errorTextClassName={classNames(authCss.customErrorTextClass, {
            [authCss.isPartnerFlow]: isPartner,
          })}
          errorMessages={!pristine || touched ? { ...submitErrors, ...errors } : {}}
          isPartner={isPartner}
          fieldOrder={fieldOrder}
          touchedFields={safeTouched}
        />
      );
    },
    [isPartner],
  );

  const getSignInButton = useCallback(
    ({
      pristine,
      submitting,
      submitSucceeded,
      hasValidationErrors,
    }: {
      pristine?: boolean;
      submitting?: boolean;
      submitSucceeded?: boolean;
      hasValidationErrors?: boolean;
    }) => {
      const SignInButton = isPartner ? PremiumGoldButton : InkButton;
      const signInButtonLabel = isPaymentFlow ? getLocalizedText(NEXT) : getLocalizedText(SIGN_IN);

      const isSubmitDisabled =
        Boolean(pristine) ||
        Boolean(submitting) ||
        Boolean(submitSucceeded) ||
        Boolean(hasValidationErrors);

      const signInButtonProps = {
        id: 'signInButton',
        type: 'submit',
        label: <span>{signInButtonLabel}</span>,
        isDisabled: isSubmitDisabled,
        className: classNames(authCss.authButton, {
          [authCss.isPartnerFlow]: isPartner,
        }),
        'data-testid': 'signInButton',
      };

      return <SignInButton {...signInButtonProps} />;
    },
    [getLocalizedText, isPartner, isPaymentFlow],
  );

  const partnerParams = useMemo(
    () => getPartnerParams(isPartner, location?.query),
    [isPartner, location?.query],
  );
  const isAlexaFlow = partnerParams?.partnerId === vars.get('ALEXA_PARTNER_ID');
  const isOnAirFlow = source === SOURCE_ONAIR;

  return (
    <Form<SignInFormValues>
      onSubmit={onSubmit}
      initialValues={{
        rememberUser: true,
      }}
    >
      {({
        errors,
        handleSubmit,
        pristine,
        submitting,
        submitSucceeded,
        submitErrors,
        hasValidationErrors,
        touched,
        values,
      }) => {
        const validFieldsMap = isPartner ? checkFieldsForValidInput(touched, errors) : null;

        return (
          <form name="signIn" onSubmit={handleSubmit}>
            <div data-testid="signIn" className={authCss.containerRestrictions}>
              {!isOnAirFlow && (
                <OauthButtons
                  actionHandler={handleOauthSignIn}
                  loggingHandler={logActions.logSignInClick}
                  isDialog={isDialog}
                  isSignIn
                  isSignUp={false}
                  isPartner={isPartner}
                  isPaymentFlow={isPaymentFlow}
                  source={source}
                  partnerParams={partnerParams}
                />
              )}
              {isPartner &&
                renderTopLevelErrorContainer(pristine, touched, submitErrors ?? {}, errors ?? {})}
              <AuthSectionDivider isPartner={isPartner} />
              <GeneralNotices
                key="signInGeneralNotices"
                failAuthNotice={failAuthNotice}
                successAuthNotice={successAuthNotice}
              />
              {!isPartner &&
                renderTopLevelErrorContainer(pristine, touched, submitErrors ?? {}, errors ?? {})}
              <div
                className={classNames(authCss.section, {
                  [authCss.noSpacing]: !isPartner,
                })}
              >
                {renderPersonalInfoFields(validFieldsMap, values)}
              </div>
              {renderCheckboxFields()}
              <div className={authCss.section}>
                <div className="row">
                  <div className="col-xs-12">
                    {shouldRenderRecaptchaV2 && (
                      <div className={css.recaptchaFallback}>
                        <div ref={recaptchaV2ContainerRef} />
                      </div>
                    )}
                    {getSignInButton({
                      pristine,
                      submitting,
                      submitSucceeded,
                      hasValidationErrors,
                    })}
                    {!isPartner && (
                      <UnderlineLink
                        key="siForgotPasswordLink"
                        /* biome-ignore lint/a11y/useSemanticElements: invalid lint error for `role` on React Component */
                        role="button"
                        tabIndex={0}
                        id="signInForgotPasswordQuestion"
                        onClick={handleChangeToForgotPassword}
                        underlineClassName={classNames(css.underlineLink, css.forgotPassword)}
                        className={classNames(authCss.link, authCss.forgotPasswordLink, {
                          [authCss.isPartnerFlow]: isPartner,
                        })}
                        isDropDown
                        isNotReactRouterLink
                      >
                        {getLocalizedText(FORGOT_PASSWORD_LINK)}
                      </UnderlineLink>
                    )}
                    {!isAlexaFlow && (
                      <SecondaryContent
                        key="signInSecondaryContent"
                        isPartnerFlow={isPartner}
                        handler={handleChangeToSignUp}
                        onSignIn
                      />
                    )}
                    {isPartner && !!queryProduct?.freeTrialLength && (
                      <AuthFlowPromoOfferFinePrint containerClassName={authCss.finePrintStyle} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        );
      }}
    </Form>
  );
}

export { SignInViewComponent as SignInView };
export default SignInViewComponent;
